import { OrbisApi } from '@orbis-systems/orbis-api-clients/orbis';
import { AxiosResponse, HttpStatusCode } from 'axios';
import { isNil } from 'lodash';
import { inject, injectable } from 'tsyringe';
import { ICache } from './Cache';
import { MiddlewareInjectToken } from './constants/tokens';
import { ApiPermissions, CacheLevel, MiddlewareConfig } from './types/index';

export class OrbisApiPermissionError extends Error {}

const API_NO_PERMISSION_RULES: Partial<
    Record<keyof ApiPermissions, { urls?: string[]; urlRegex?: RegExp; returnData: any }[]>
> = {
    advisor: [
        {
            urlRegex: /\/(branch|advisory)\/.*/,
            returnData: new OrbisApiPermissionError('No advisor permission'),
        },
    ],
    rtbs: [
        {
            urls: ['v2/advisory/model/rtbs', 'v1/branch/rtbs', 'user/rtb/history'],
            returnData: [],
        },
        {
            urls: ['user/rtb'],
            returnData: {},
        },
    ],
    documents: [
        {
            urls: ['documents/service'],
            returnData: [],
        },
    ],
    iav: [
        {
            urls: ['iav/init'],
            returnData: { publicKey: '' },
        },
        {
            urls: ['iav/complete', 'transfer/relationship/cancel'],
            returnData: new OrbisApiPermissionError('No IAV permission'),
        },
    ],
    transfers: [
        {
            urls: ['transfer/relationship/list', 'transfer/status'],
            returnData: [],
        },
        {
            urls: ['transfer/available'],
            returnData: 0,
        },
        {
            urlRegex: /\/transfer\/.*/,
            returnData: new OrbisApiPermissionError('No transfer permission'),
        },
    ],
};

@injectable()
export class OrbisApiWithPermissions extends OrbisApi {
    constructor(
        @inject(MiddlewareInjectToken.OrbisApi) orbisApi: OrbisApi,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
        @inject(MiddlewareInjectToken.Cache) cache: ICache,
    ) {
        super(orbisApi.getApiClient().getConfig());

        const originalAxiosInstance = orbisApi.getApiClient().getAxios();

        this.getApiClient().getAxios().defaults.adapter = (requestConfig) => {
            return new Promise((resolve, reject) => {
                // Make sure this adapter is removed before passing the config to original axios instance to avoid circular calls
                requestConfig.adapter = undefined;

                const { hasPermission, returnData } = this.checkPermission(requestConfig.url!, config);

                if (returnData instanceof Error) {
                    return reject(returnData);
                }

                if (!hasPermission) {
                    return resolve({
                        data: returnData,
                        config: requestConfig,
                        status: HttpStatusCode.Ok,
                        statusText: '',
                        headers: {},
                    });
                }

                if (config.cacheLevel === CacheLevel.NoCache) {
                    return originalAxiosInstance.request(requestConfig).then(resolve).catch(reject);
                }

                const cacheKey = JSON.stringify(requestConfig);
                const response = cache.get(cacheKey);

                if (!isNil(response)) {
                    if (!(response instanceof Promise)) {
                        return resolve(response as AxiosResponse);
                    }
                } else {
                    cache.del(cacheKey);

                    const promise = originalAxiosInstance.request(requestConfig);

                    promise
                        .then((response) => {
                            cache.set(cacheKey, response, config.cacheDuration);
                            resolve(response);
                        })
                        .catch((error: Error) => {
                            cache.del(cacheKey);
                            reject(error);
                        });

                    cache.set(cacheKey, promise, config.cacheDuration);
                    return promise;
                }
            });
        };
    }

    private checkPermission(
        url: string,
        config: MiddlewareConfig,
    ): {
        hasPermission: boolean;
        returnData?: any;
    } {
        const apis = Object.keys(config.apiPermissions) as (keyof ApiPermissions)[];
        for (const api of apis) {
            const rules = API_NO_PERMISSION_RULES[api];
            if (!config.apiPermissions[api] && rules) {
                for (const item of rules) {
                    if (item.urls?.includes(url) || item.urlRegex?.test(url)) {
                        return {
                            hasPermission: false,
                            returnData: item.returnData,
                        };
                    }
                }
            }
        }

        return {
            hasPermission: true,
        };
    }
}
